import { capturePosthogEvent, readLineCtaId, registerLineClick } from "@/lib/line-click";
import { LP_SESSION_STORAGE_KEY, LP_VISIT_SIG_STORAGE_KEY } from "@/lib/lp-line";
import { useEffect, useRef, useCallback } from "react";
import { writeLpAttributionIfNew } from "@/lib/attribution";
import { sitePath } from "../lib/site-path";
import { chunkLpEvents } from "../lib/lp-events-chunk";
import { safeParamValue, sanitizeAnalyticsUrl, sanitizeReferrer } from "../lib/analytics-url";

// Safari/iOS lacks requestIdleCallback; fall back to setTimeout so handlers never throw.
function safeIdleCallback(cb: () => void): void {
  if (typeof window === "undefined") return;
  const ric = (window as any).requestIdleCallback;
  if (typeof ric === "function") {
    ric(cb, { timeout: 2000 });
  } else {
    setTimeout(cb, 1);
  }
}

// セッションIDを生成（ページビュー単位）
function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

// 訪問者IDを取得または生成（ブラウザに永続化）
function getOrCreateVisitorId(): string {
  const STORAGE_KEY = "shegate_visitor_id";
  let visitorId = localStorage.getItem(STORAGE_KEY);
  if (!visitorId) {
    visitorId = `v_${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(STORAGE_KEY, visitorId);
  }
  return visitorId;
}

// デバイスタイプを判定
function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

// 流入元を自動判定（大分類: meta, google, organic, direct など）
function getSource(): string {
  const params = new URLSearchParams(window.location.search);
  // Meta広告
  if (params.get("fbclid")) return "meta";
  // Google広告
  if (params.get("gclid")) return "google";
  // UTMソースから判定
  const utmSource = params.get("utm_source")?.toLowerCase();
  if (utmSource) {
    if (utmSource.includes("facebook") || utmSource.includes("instagram") || utmSource.includes("meta")) return "meta";
    if (utmSource.includes("google")) return "google";
    if (utmSource.includes("yahoo")) return "yahoo";
    if (utmSource.includes("line")) return "line";
    return utmSource;
  }
  // リファラーから判定
  if (document.referrer) {
    try {
      const referrerHost = new URL(document.referrer).hostname;
      if (referrerHost.includes("google")) return "organic_google";
      if (referrerHost.includes("yahoo")) return "organic_yahoo";
      if (referrerHost.includes("bing")) return "organic_bing";
      return "referral";
    } catch {
      return "referral";
    }
  }
  return "direct";
}

// 広告クリエイティブを取得（fromパラメータ）
function getAdCreative(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  return safeParamValue(params.get("from"));
}

// 紹介コードを取得・永続化（?ref=パラメータ）
function getReferralCode(): string | undefined {
  const STORAGE_KEY = "shegate_ref_code";
  const params = new URLSearchParams(window.location.search);
  const refCode = params.get("ref");
  if (refCode) {
    localStorage.setItem(STORAGE_KEY, refCode);
    return refCode;
  }
  return localStorage.getItem(STORAGE_KEY) || undefined;
}

// UTMパラメータを取得
function getTrackingParams(): {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
} {
  const params = new URLSearchParams(window.location.search);

  // fbcをcookieから取得
  const getFbc = (): string | undefined => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; _fbc=`);
    if (parts.length === 2) return parts.pop()?.split(";").shift();
    // cookieがなければfbclidから生成
    const fbclid = params.get("fbclid");
    if (fbclid) return `fb.1.${Date.now()}.${fbclid}`;
    return undefined;
  };

  // fbpをcookieから取得
  const getFbp = (): string | undefined => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; _fbp=`);
    if (parts.length === 2) return parts.pop()?.split(";").shift();
    return undefined;
  };

  return {
    utmSource: safeParamValue(params.get("utm_source")),
    utmMedium: safeParamValue(params.get("utm_medium")),
    utmCampaign: safeParamValue(params.get("utm_campaign")),
    fbclid: params.get("fbclid") || undefined,
    fbc: getFbc(),
    fbp: getFbp(),
  };
}

// 現在表示中のセクションを取得
function getCurrentSection(sectionIds: string[]): string | undefined {
  for (const id of sectionIds) {
    const element = document.getElementById(id);
    if (element) {
      const rect = element.getBoundingClientRect();
      if (rect.top <= window.innerHeight / 2 && rect.bottom > 0) {
        return id;
      }
    }
  }
  return sectionIds[0];
}

type LpAnalyticsOptions = {
  sectionIds?: string[];
  videoSelector?: string;
  ctaSelector?: string;
  tocSelector?: string;
  pageUrlOverride?: string; // A/Bテスト等でURLを上書きしたい場合
};

// LINE の行き先か。/women/line/ は LP の LINE ボタンの中継 (その先で LINE の計測リンクへ転送する)。
// 外すと LINE を押した記録が残らず、友だち追加と LP の訪問を突き合わせられなくなる
function isLineHref(href: string): boolean {
  return href.includes("line.blueai.jp") || href.includes("line.me") || href.includes("/women/line/");
}

export function useLpAnalytics(options: LpAnalyticsOptions = {}) {
  const {
    sectionIds = [],
    videoSelector = "video",
    ctaSelector = '[data-cta="true"]',
    tocSelector = '[data-toc="true"]',
    pageUrlOverride,
  } = options;

  const sessionIdRef = useRef<string | null>(null);
  const visitorIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const maxScrollDepthRef = useRef<number>(0);
  const scrollMilestonesRef = useRef<Set<number>>(new Set());
  const viewedSectionsRef = useRef<Set<string>>(new Set());
  const videoMilestonesRef = useRef<Map<string, Set<number>>>(new Map());
  const rageClickCountRef = useRef(0);
  const lastClickTimeRef = useRef<number>(0);
  const visibilityChangesRef = useRef(0);
  const errorCountRef = useRef(0);
  const eventQueueRef = useRef<
    Array<{
      sessionId: string;
      eventType: string;
      eventValue?: string;
      timeFromStart?: number;
    }>
  >([]);
  const isVisitStartedRef = useRef(false);
  // サーバーが visit_start で返した署名。届くまでは無い (署名なしの line_clicked は移行の期間だけ通る)
  const visitSigRef = useRef<string | undefined>(undefined);
  // 署名が届く前に LINE を押したら、届いた時点で署名つきで送り直す
  const lineClickAwaitingSigRef = useRef<{ dwellTime: number; maxScrollDepth: number } | null>(
    null
  );

  // イベントをキューに追加
  const queueEvent = useCallback((eventType: string, eventValue?: string) => {
    if (!sessionIdRef.current) return;

    const timeFromStart = Math.floor((Date.now() - startTimeRef.current) / 1000);
    eventQueueRef.current.push({
      sessionId: sessionIdRef.current,
      eventType,
      eventValue,
      timeFromStart,
    });
  }, []);

  // イベントキューを送信
  const flushEvents = useCallback(async () => {
    if (eventQueueRef.current.length === 0) return;

    const events = [...eventQueueRef.current];
    eventQueueRef.current = [];

    // 50 件ずつ送る。失敗した塊とそれ以降だけキューへ戻す
    const chunks = chunkLpEvents(events);
    let sent = 0;
    try {
      for (const chunk of chunks) {
        await fetch(sitePath("/api/lp-analytics"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: "events", events: chunk, visitSig: visitSigRef.current }),
        });
        sent += chunk.length;
      }
    } catch (error) {
      console.error("Failed to send LP events:", error);
      eventQueueRef.current = [...events.slice(sent), ...eventQueueRef.current];
    }
  }, []);

  // 訪問更新を送信
  const updateVisit = useCallback(
    async (data: {
      dwellTime?: number;
      maxScrollDepth?: number;
      exitSection?: string;
      lineClicked?: boolean;
    }) => {
      if (!sessionIdRef.current) return;

      try {
        await fetch(sitePath("/api/lp-analytics"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "visit_update",
            sessionId: sessionIdRef.current,
            visitSig: visitSigRef.current,
            ...data,
          }),
        });
      } catch (error) {
        console.error("Failed to update LP visit:", error);
      }
    },
    []
  );

  // LINEクリックを記録。同じ押下を capture の自動記録と onClick の手書きの両方が呼んでも、1回だけ数える
  const trackLineClick = useCallback((rawCtaId?: unknown) => {
    const { eventId, duplicate } = registerLineClick();
    if (duplicate) return;
    // 押された位置 (hero、final、sticky など)。onClick に直接渡されたイベントなどは位置として読まない
    const ctaId = typeof rawCtaId === "string" ? rawCtaId : undefined;
    queueEvent("cta_click", "line");
    // cta_click の値は line のまま (ダッシュボードの約束)。位置は別の印で残す
    if (ctaId) queueEvent("line_cta_click", ctaId);
    // 滞在時間とスクロール深度も一緒に送信
    const dwellTime = startTimeRef.current
      ? Math.round((Date.now() - startTimeRef.current) / 1000)
      : 0;
    // The LINE button navigates away immediately, so a plain fetch is killed
    // before it reaches the server — only ~1.7% of clicks were being recorded.
    // sendBeacon survives the navigation and guarantees delivery; this is what
    // the LINE webhook time-window matcher reads to fire the Meta conversion.
    const sessionId = sessionIdRef.current;
    if (sessionId && typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(
        sitePath("/api/lp-analytics"),
        JSON.stringify({
          type: "visit_update",
          sessionId,
          visitSig: visitSigRef.current,
          lineClicked: true,
          dwellTime,
          maxScrollDepth: maxScrollDepthRef.current,
        })
      );
      if (!visitSigRef.current) {
        lineClickAwaitingSigRef.current = { dwellTime, maxScrollDepth: maxScrollDepthRef.current };
      }
    } else {
      updateVisit({
        lineClicked: true,
        dwellTime,
        maxScrollDepth: maxScrollDepthRef.current,
      });
    }

    // PostHog にも送る。広告 LP は SDK を読まないので、そこでは直接送る。
    // eventId は Meta の Lead と同じ値で、PostHog と Meta を突き合わせられる
    capturePosthogEvent(
      "line_click",
      {
        page: pageUrlOverride || window.location.pathname,
        dwellTime,
        scrollDepth: maxScrollDepthRef.current,
        source: getSource(),
        deviceType: getDeviceType(),
        eventId,
        ...(ctaId ? { ctaId } : {}),
      },
      visitorIdRef.current,
    );

    flushEvents();
  }, [queueEvent, updateVisit, flushEvents, pageUrlOverride]);

  // LP ごとの見せ場の計測 (FAQ を開いた、動きの物語を最後まで見た など)。
  // 送り方は scroll_* や cta_view と同じで、lp_events のキューと PostHog の両方へ出す
  const trackEvent = useCallback(
    (eventType: string, eventValue?: string) => {
      queueEvent(eventType, eventValue);
      if (typeof window !== "undefined" && (window as any).posthog) {
        safeIdleCallback(() => {
          (window as any).posthog.capture(eventType, {
            page: pageUrlOverride || window.location.pathname,
            eventValue,
            dwellTime: Math.round((Date.now() - startTimeRef.current) / 1000),
            scrollDepth: maxScrollDepthRef.current,
            source: getSource(),
            deviceType: getDeviceType(),
          });
        });
      }
    },
    [queueEvent, pageUrlOverride]
  );

  // 訪問開始
  useEffect(() => {
    if (isVisitStartedRef.current) return;
    isVisitStartedRef.current = true;

    const sessionId = generateSessionId();
    const visitorId = getOrCreateVisitorId();
    sessionIdRef.current = sessionId;
    visitorIdRef.current = visitorId;
    try {
      window.sessionStorage.setItem(LP_SESSION_STORAGE_KEY, sessionId);
    } catch {
      // 置けなくても計測は続ける (LIFF を通さず従来の LINE へ行くだけ)
    }
    startTimeRef.current = Date.now();

    const trackingParams = getTrackingParams();
    const source = getSource();
    const adCreative = getAdCreative();
    const referralCode = getReferralCode();

    // pageUrlOverride がある場合はそれを使用（A/Bテスト用）
    const pageUrl = pageUrlOverride
      ? sanitizeAnalyticsUrl(new URL(pageUrlOverride, window.location.origin).href)
      : sanitizeAnalyticsUrl(window.location.href);

    // Persist attribution to sessionStorage so reservation form submits can
    // forward it to the API (counseling_reservations / trial_lesson_reservations).
    // Only writes on first paid/UTM-tagged visit — later organic visits won't
    // overwrite earlier paid attribution within the same session.
    writeLpAttributionIfNew({
      ad_creative: adCreative ?? null,
      source: source ?? null,
      utm_source: trackingParams.utmSource ?? null,
      utm_medium: trackingParams.utmMedium ?? null,
      utm_campaign: trackingParams.utmCampaign ?? null,
      fbclid: trackingParams.fbclid ?? null,
      landing_page: pageUrlOverride || window.location.pathname,
      visitor_id: visitorId,
    });

    const visitStartBody = JSON.stringify({
      type: "visit_start",
      sessionId,
      visitorId,
      pageUrl,
      referrer: sanitizeReferrer(document.referrer),
      deviceType: getDeviceType(),
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      source,
      adCreative,
      referralCode,
      ...trackingParams,
    });
    fetch(sitePath("/api/lp-analytics"), {
      method: "POST",
      // 開いてすぐ LINE を押して移っても訪問の開始を切らせない (LINE へは待たずに移る)。
      // keepalive の枠 (送信中の合計 64KB) は LINE の Lead と分け合うので、8KB を越える本文には付けない
      keepalive: visitStartBody.length < 8192,
      headers: { "Content-Type": "application/json" },
      body: visitStartBody,
    })
      .then((res) => res.json() as Promise<{ visitSig?: unknown }>)
      .then((json) => {
        const sig = json?.visitSig;
        if (typeof sig !== "string" || !sig) return;
        visitSigRef.current = sig;
        try {
          window.sessionStorage.setItem(LP_VISIT_SIG_STORAGE_KEY, JSON.stringify({ sessionId, sig }));
        } catch {
          // 置けなくても手元の ref で送る
        }
        const awaiting = lineClickAwaitingSigRef.current;
        if (awaiting && navigator.sendBeacon) {
          lineClickAwaitingSigRef.current = null;
          navigator.sendBeacon(
            sitePath("/api/lp-analytics"),
            JSON.stringify({ type: "visit_update", sessionId, visitSig: sig, lineClicked: true, lineClickResend: true, ...awaiting })
          );
        }
      })
      .catch(() => {
        // 計測の応答が読めなくても画面は止めない
      });

    // PostHogに訪問開始を送信
    if (typeof window !== "undefined" && (window as any).posthog) {
      (window as any).posthog.capture("lp_visit_start", {
        page: pageUrlOverride || window.location.pathname,
        source,
        adCreative,
        deviceType: getDeviceType(),
        referrer: sanitizeReferrer(document.referrer),
        utmSource: trackingParams.utmSource,
        utmMedium: trackingParams.utmMedium,
        utmCampaign: trackingParams.utmCampaign,
        fbclid: trackingParams.fbclid,
      });
    }
  }, [pageUrlOverride]);

  // スクロールトラッキング
  useEffect(() => {
    let scrollTimer: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);

      if (scrollPercent > maxScrollDepthRef.current) {
        maxScrollDepthRef.current = scrollPercent;
      }

      const milestones = [25, 50, 75, 100];
      for (const milestone of milestones) {
        if (scrollPercent >= milestone && !scrollMilestonesRef.current.has(milestone)) {
          scrollMilestonesRef.current.add(milestone);
          queueEvent(`scroll_${milestone}`);

          // PostHogにスクロールイベント送信（非同期）
          if (typeof window !== "undefined" && (window as any).posthog) {
            safeIdleCallback(() => {
              (window as any).posthog.capture(`scroll_${milestone}`, {
                page: pageUrlOverride || window.location.pathname,
                dwellTime: Math.round((Date.now() - startTimeRef.current) / 1000),
                source: getSource(),
                deviceType: getDeviceType(),
              });
            });
          }
        }
      }

      for (const id of sectionIds) {
        if (viewedSectionsRef.current.has(id)) continue;

        const element = document.getElementById(id);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            viewedSectionsRef.current.add(id);
            queueEvent("section_view", id);
          }
        }
      }
      }, 100); // デバウンス: 100ms
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      clearTimeout(scrollTimer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [sectionIds, queueEvent, pageUrlOverride]);

  // 動画トラッキング
  useEffect(() => {
    const videos = document.querySelectorAll<HTMLVideoElement>(videoSelector);

    const handleTimeUpdate = (video: HTMLVideoElement, index: number) => {
      const videoId = video.id || `video-${index}`;
      const progress = Math.round((video.currentTime / video.duration) * 100);

      if (!videoMilestonesRef.current.has(videoId)) {
        videoMilestonesRef.current.set(videoId, new Set());
      }
      const milestones = videoMilestonesRef.current.get(videoId)!;

      const thresholds = [25, 50, 75, 100];
      for (const threshold of thresholds) {
        if (progress >= threshold && !milestones.has(threshold)) {
          milestones.add(threshold);
          queueEvent(`video_${threshold}`, videoId);

          // PostHogに動画イベント送信（非同期）
          if (typeof window !== "undefined" && (window as any).posthog) {
            safeIdleCallback(() => {
              (window as any).posthog.capture(`video_progress_${threshold}`, {
                page: pageUrlOverride || window.location.pathname,
                videoId,
                dwellTime: Math.round((Date.now() - startTimeRef.current) / 1000),
              });
            });
          }
        }
      }
    };

    const handlePlay = (video: HTMLVideoElement, index: number) => {
      const videoId = video.id || `video-${index}`;
      queueEvent("video_play", videoId);

      // PostHogに動画再生イベント送信
      if (typeof window !== "undefined" && (window as any).posthog) {
        (window as any).posthog.capture("video_play", {
          page: pageUrlOverride || window.location.pathname,
          videoId,
          dwellTime: Math.round((Date.now() - startTimeRef.current) / 1000),
        });
      }
    };

    const listeners: Array<{ video: HTMLVideoElement; event: string; handler: () => void }> = [];

    videos.forEach((video, index) => {
      const playHandler = () => handlePlay(video, index);
      const timeHandler = () => handleTimeUpdate(video, index);

      video.addEventListener("play", playHandler);
      video.addEventListener("timeupdate", timeHandler);

      listeners.push({ video, event: "play", handler: playHandler });
      listeners.push({ video, event: "timeupdate", handler: timeHandler });
    });

    return () => {
      listeners.forEach(({ video, event, handler }) => {
        video.removeEventListener(event, handler);
      });
    };
  }, [videoSelector, queueEvent, pageUrlOverride]);

  // CTAビュートラッキング
  useEffect(() => {
    const ctaElements = document.querySelectorAll(ctaSelector);
    const viewedCtas = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const ctaId =
              (entry.target as HTMLElement).dataset.ctaId ||
              entry.target.id ||
              "unknown";
            if (!viewedCtas.has(ctaId)) {
              viewedCtas.add(ctaId);
              queueEvent("cta_view", ctaId);

              // PostHogにCTAビューイベント送信（非同期）
              if (typeof window !== "undefined" && (window as any).posthog) {
                safeIdleCallback(() => {
                  (window as any).posthog.capture("cta_view", {
                    page: pageUrlOverride || window.location.pathname,
                    ctaId,
                    dwellTime: Math.round((Date.now() - startTimeRef.current) / 1000),
                  });
                });
              }
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    ctaElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [ctaSelector, queueEvent, pageUrlOverride]);

  // TOCクリックトラッキング
  useEffect(() => {
    const tocLinks = document.querySelectorAll(tocSelector);

    const handleClick = (e: Event) => {
      const target = e.currentTarget as HTMLElement;
      const sectionId = target.dataset.tocTarget || target.getAttribute("href")?.replace("#", "");
      if (sectionId) {
        queueEvent("toc_click", sectionId);

        // PostHogにTOCクリックイベント送信
        if (typeof window !== "undefined" && (window as any).posthog) {
          (window as any).posthog.capture("toc_click", {
            page: pageUrlOverride || window.location.pathname,
            sectionId,
            dwellTime: Math.round((Date.now() - startTimeRef.current) / 1000),
          });
        }
      }
    };

    tocLinks.forEach((link) => {
      link.addEventListener("click", handleClick);
    });

    return () => {
      tocLinks.forEach((link) => {
        link.removeEventListener("click", handleClick);
      });
    };
  }, [tocSelector, queueEvent, pageUrlOverride]);

  // 早期バウンス検出用タイマー
  useEffect(() => {
    const timer3s = setTimeout(() => {
      if (typeof window !== "undefined" && (window as any).posthog) {
        (window as any).posthog.capture("stayed_3_seconds", {
          page: pageUrlOverride || window.location.pathname,
          scrollDepth: maxScrollDepthRef.current,
          source: getSource(),
          deviceType: getDeviceType(),
        });
      }
    }, 3000);

    const timer10s = setTimeout(() => {
      if (typeof window !== "undefined" && (window as any).posthog) {
        (window as any).posthog.capture("stayed_10_seconds", {
          page: pageUrlOverride || window.location.pathname,
          scrollDepth: maxScrollDepthRef.current,
          source: getSource(),
          deviceType: getDeviceType(),
        });
      }
    }, 10000);

    return () => {
      clearTimeout(timer3s);
      clearTimeout(timer10s);
    };
  }, [pageUrlOverride]);

  // Rage click検出
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // ボタンやリンクのみ監視（パフォーマンス向上）
      const target = e.target as HTMLElement;
      if (!target.matches('button, a, [data-cta="true"], [role="button"]')) {
        return;
      }
      const now = Date.now();
      if (now - lastClickTimeRef.current < 500) {
        rageClickCountRef.current++;
        if (rageClickCountRef.current >= 5) {
          if (typeof window !== "undefined" && (window as any).posthog) {
            (window as any).posthog.capture("rage_click", {
              page: pageUrlOverride || window.location.pathname,
              dwellTime: Math.round((Date.now() - startTimeRef.current) / 1000),
              scrollDepth: maxScrollDepthRef.current,
            });
          }
          rageClickCountRef.current = 0;
        }
      } else {
        rageClickCountRef.current = 0;
      }
      lastClickTimeRef.current = now;
    };

    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, [pageUrlOverride]);

  // LINE CTA クリックの自動記録。
  // 一部の LP（2606-a 等）は LINE ボタンを onClick なしの素の <a> で置いており、
  // クリックが line_clicked に記録されず Meta CV が一切マッチしなかった。
  // ページ側の配線に頼らず、LINE 行き先のアンカーを capture フェーズで拾って
  // trackLineClick（sendBeacon）を必ず発火させ、どの LP でも CV を成立させる。
  useEffect(() => {
    const handleLineAnchorClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement)?.closest?.("a");
      const href = anchor?.getAttribute("href") || "";
      if (isLineHref(href)) {
        trackLineClick(readLineCtaId(anchor));
      }
    };
    window.addEventListener("click", handleLineAnchorClick, { capture: true });
    return () =>
      window.removeEventListener("click", handleLineAnchorClick, {
        capture: true,
      });
  }, [trackLineClick]);

  // LINE 以外に着地する CTA のクリック記録。
  // cta_view は data-cta-id ごとに取れているのに、cta_click は上の LINE 経路
  // からしか発火していなかった。カウンセリング予約に飛ばす LP（increase-2608 等）
  // では、どの位置の CTA が押されたのかが本番に1件も残らない状態だったので拾う。
  // LINE 行きは trackLineClick が既に送っているため、二重計上しないよう除外する。
  useEffect(() => {
    const handleCtaAnchorClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement)?.closest?.("a");
      if (!anchor || anchor.dataset.cta !== "true") return;
      const href = anchor.getAttribute("href") || "";
      if (isLineHref(href)) return;

      const ctaId = anchor.dataset.ctaId || "unknown";
      const dwellTime = startTimeRef.current
        ? Math.round((Date.now() - startTimeRef.current) / 1000)
        : 0;

      queueEvent("cta_click", ctaId);

      // 別タブで開く CTA でも、同タブ遷移に変われば fetch は途中で殺される。
      // LINE 側と同じ理由で sendBeacon を先に撃つ
      const sessionId = sessionIdRef.current;
      const events = [...eventQueueRef.current];
      if (
        sessionId &&
        events.length > 0 &&
        typeof navigator !== "undefined" &&
        navigator.sendBeacon
      ) {
        eventQueueRef.current = [];
        for (const chunk of chunkLpEvents(events)) {
          navigator.sendBeacon(
            sitePath("/api/lp-analytics"),
            JSON.stringify({ type: "events", events: chunk, visitSig: visitSigRef.current })
          );
        }
      } else {
        flushEvents();
      }

      if (typeof window !== "undefined" && (window as any).posthog) {
        (window as any).posthog.capture("cta_click", {
          page: pageUrlOverride || window.location.pathname,
          ctaId,
          href,
          dwellTime,
          scrollDepth: maxScrollDepthRef.current,
          source: getSource(),
          deviceType: getDeviceType(),
        });
      }
    };
    window.addEventListener("click", handleCtaAnchorClick, { capture: true });
    return () =>
      window.removeEventListener("click", handleCtaAnchorClick, {
        capture: true,
      });
  }, [queueEvent, flushEvents, pageUrlOverride]);

  // タブ切り替え検出
  useEffect(() => {
    const handleVisibilityChange = () => {
      visibilityChangesRef.current++;
      if (typeof window !== "undefined" && (window as any).posthog) {
        (window as any).posthog.capture("tab_visibility_change", {
          page: pageUrlOverride || window.location.pathname,
          isHidden: document.hidden,
          changeCount: visibilityChangesRef.current,
          dwellTime: Math.round((Date.now() - startTimeRef.current) / 1000),
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [pageUrlOverride]);

  // JavaScriptエラー検出
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      errorCountRef.current++;
      if (typeof window !== "undefined" && (window as any).posthog) {
        (window as any).posthog.capture("javascript_error", {
          page: pageUrlOverride || window.location.pathname,
          error: event.message,
          source: event.filename,
          line: event.lineno,
          column: event.colno,
          errorCount: errorCountRef.current,
        });
      }
    };

    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, [pageUrlOverride]);

  // 定期的にイベントを送信（5秒ごと）
  useEffect(() => {
    const interval = setInterval(flushEvents, 5000);
    return () => clearInterval(interval);
  }, [flushEvents]);

  // 離脱時に最終データを送信
  useEffect(() => {
    const handleBeforeUnload = () => {
      const dwellTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const exitSection = getCurrentSection(sectionIds);

      navigator.sendBeacon(
        sitePath("/api/lp-analytics"),
        JSON.stringify({
          type: "visit_update",
          sessionId: sessionIdRef.current,
          visitSig: visitSigRef.current,
          dwellTime,
          maxScrollDepth: maxScrollDepthRef.current,
          exitSection,
        })
      );

      if (eventQueueRef.current.length > 0) {
        for (const chunk of chunkLpEvents(eventQueueRef.current)) {
          navigator.sendBeacon(
            sitePath("/api/lp-analytics"),
            JSON.stringify({ type: "events", events: chunk, visitSig: visitSigRef.current })
          );
        }
        // beforeunload と pagehide の両方で呼ばれるので、送った分は空にして二重に数えない
        eventQueueRef.current = [];
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handleBeforeUnload);

      // PostHogに離脱イベント送信
      if (typeof window !== "undefined" && (window as any).posthog) {
        const dwellTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const exitSection = getCurrentSection(sectionIds);

        (window as any).posthog.capture("lp_page_exit", {
          page: pageUrlOverride || window.location.pathname,
          dwellTime,
          maxScrollDepth: maxScrollDepthRef.current,
          exitSection,
          source: getSource(),
          deviceType: getDeviceType(),
          tabChanges: visibilityChangesRef.current,
          errorCount: errorCountRef.current,
        });
      }
    };
  }, [sectionIds, pageUrlOverride]);

  return {
    sessionId: sessionIdRef.current,
    visitorId: visitorIdRef.current,
    trackLineClick,
    trackEvent,
  };
}
